import React, { useContext } from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCheckCircle, 
  faTimesCircle, 
  faExclamationCircle 
} from '@fortawesome/free-solid-svg-icons';
import { ThemeContext } from '../context/ThemeProvider';
import { getStudyIconBg } from '../utils/themeUtils';

const AnswerMessage = ({ correct_answer, selected_answer }) => {
  const { theme, currentTheme } = useContext(ThemeContext);
  
  // Determine answer status
  const isMultipleChoice = Array.isArray(correct_answer);
  const cleanedSelectedAnswers = Array.isArray(selected_answer) 
    ? selected_answer.filter(answer => answer !== 'X') 
    : selected_answer;
  const sortedSelectedAnswers = Array.isArray(cleanedSelectedAnswers) 
    ? [...cleanedSelectedAnswers].sort() 
    : cleanedSelectedAnswers;
  const sortedCorrectAnswers = isMultipleChoice 
    ? [...correct_answer].sort() 
    : correct_answer;

  // Helper function to get icon background color
  const getIconBg = () => {
    return getStudyIconBg(theme, currentTheme);
  };

  // Check if answer is correct
  const isCorrectAnswer = () => {
    if (isMultipleChoice) {
      if (sortedCorrectAnswers.length !== sortedSelectedAnswers.length) {
        return false;
      }
      for (let i = 0; i < sortedCorrectAnswers.length; i++) {
        if (sortedCorrectAnswers[i] !== sortedSelectedAnswers[i]) {
          return false;
        }
      }
      return true;
    } else {
      return correct_answer === selected_answer;
    }
  };

  // Check if question was not answered
  const didNotAnswer = () => {
    if (Array.isArray(selected_answer)) {
      return selected_answer.length === 1 && selected_answer[0] === 'X';
    } else {
      return selected_answer === 'X';
    }
  };

  // Animation variants
  const messageVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.3,
        type: "spring",
        stiffness: 300,
        damping: 20
      }
    }
  };

  // Message config based on answer status
  const messageConfig = didNotAnswer()
    ? {
        icon: faExclamationCircle,
        color: currentTheme === 'light' ? '#9ca3af' : '#6b7280',
        backgroundColor: currentTheme === 'light' ? 'rgba(243,244,246,0.8)' : 'rgba(31,41,55,0.8)',
        title: 'You did not answer this question.',
        message: null
      }
    : isCorrectAnswer()
    ? {
        icon: faCheckCircle,
        color: '#10b981',
        backgroundColor: 'rgba(16,185,129,0.1)',
        title: 'Great job! You got it right.',
        message: null
      }
    : {
        icon: faTimesCircle,
        color: '#ef4444',
        backgroundColor: 'rgba(239,68,68,0.1)',
        title: 'Not quite!',
        message: `You selected "${Array.isArray(sortedSelectedAnswers) ? sortedSelectedAnswers.join(', ') : sortedSelectedAnswers}", but the correct answer is "${Array.isArray(sortedCorrectAnswers) ? sortedCorrectAnswers.join(', ') : sortedCorrectAnswers}"`
      };

  return (
    <motion.div
      initial="initial"
      animate="animate"
      variants={messageVariants}
      className="mb-6 rounded-lg overflow-hidden"
      style={{ backgroundColor: messageConfig.backgroundColor }}
    >
      <div className="p-4">
        <div className="flex items-start">
          <div className="mr-3 mt-0.5">
            <FontAwesomeIcon 
              icon={messageConfig.icon} 
              className="text-2xl"
              style={{ color: messageConfig.color }}
            />
          </div>
          
          <div>
            <p 
              className="font-semibold text-lg mb-1"
              style={{ color: messageConfig.color }}
            >
              {messageConfig.title}
            </p>
            
            {messageConfig.message && (
              <p 
                className="text-gray-600 dark:text-gray-300"
              >
                {messageConfig.message}
              </p>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default AnswerMessage;